.dial {
  position: relative;
  background-color: var(--surface);
  padding: var(--space-3) var(--space-3) var(--space-2);
  user-select: none;
  -webkit-user-select: none;
  container-type: inline-size;
}

.disabled {
  opacity: 0.55;
}

.top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.now {
  min-height: 32px;
  padding: 0 var(--space-3);
  border: 1px solid var(--control);
  border-radius: var(--radius-m);
  background: none;
  font: 500 var(--text-xs) / 1 var(--font-mono);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  transition: background-color var(--duration-fast) var(--ease-out);
}

.now:hover:not(:disabled) {
  background: var(--surface-sunken);
}
.now:disabled {
  opacity: 0.45;
  cursor: default;
}

.days {
  position: relative; /* offsetLeft de cada día relativo a la fila que desplaza (centrado del día activo) */
  display: flex;
  gap: var(--space-1);
  margin-top: var(--space-2);
  padding-inline-end: var(--space-5);
  overflow-x: auto;
  scroll-snap-type: x proximity;
  scrollbar-width: none;
  /* Hay más días a la derecha: el borde se desvanece (con el relleno final, el último día sale del fundido). */
  mask-image: linear-gradient(90deg, #000 calc(100% - var(--space-5)), transparent);
}

/* Deshabilitado no hay nada enfocable: una región desplazable sin foco incumple WCAG (axe scrollable-region-focusable). */
.disabled .days {
  overflow-x: hidden;
}

.days::-webkit-scrollbar {
  display: none;
}

.day {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  flex: 0 0 auto;
  min-width: 44px;
  min-height: 44px;
  padding: 0 var(--space-3);
  scroll-snap-align: center;
  border-bottom: 2px solid transparent;
  color: var(--ink-muted);
  font: 500 var(--text-xs) / 1 var(--font-mono);
  letter-spacing: 0.04em;
  text-transform: lowercase;
  cursor: pointer;
}

.day input {
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
}

.day:has(input:checked) {
  color: var(--ink);
  border-bottom-color: var(--accent);
}

/* Sombra interior: el contorno lo recortaría la fila con desplazamiento. */
.day:has(input:focus-visible) {
  box-shadow: inset 0 0 0 2px var(--accent);
  border-radius: var(--radius-m);
}

/* Orientativo: «≈» delante y una raya discontinua corta propia de cada día (no una línea continua entre días). */
.approx {
  color: var(--ink-subtle);
}

.orientative::after {
  content: "";
  position: absolute;
  inset: auto var(--space-3) 6px;
  border-bottom: 1px dashed var(--ink-subtle);
}

.orientative:has(input:checked)::after {
  display: none;
}

.day:has(input:disabled) {
  cursor: default;
}

/* Nota visible del día orientativo; el hueco se reserva para que el dial no salte al cambiar de día. */
.dayNote {
  min-height: calc(var(--text-xs) * 1.4);
  margin-top: var(--space-1);
  font: 500 var(--text-xs) / 1.4 var(--font-mono);
  letter-spacing: 0.04em;
  color: var(--ink-muted);
}

.face {
  position: relative;
  cursor: grab;
  touch-action: pan-y;
  outline-offset: 4px;
  border-radius: var(--radius-m);
}

.dragging {
  cursor: grabbing;
}

.svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.rotor {
  transform-box: view-box;
  transition: transform var(--duration) var(--ease-out);
  will-change: transform;
}

/* Durante arrastre e inercia la rotación sigue al dedo sin interpolar. */
.free {
  transition: none;
}

.minor {
  stroke: var(--ink-subtle);
  stroke-width: 1;
}
.half {
  stroke: var(--ink-muted);
  stroke-width: 1.2;
}
.major {
  stroke: var(--ink);
  stroke-width: 1.6;
}
.hour {
  font: 500 11px var(--font-mono);
  fill: var(--ink-muted);
  font-variant-numeric: tabular-nums;
}

.band {
  opacity: 0.35;
  transition: opacity var(--duration) var(--ease-out);
}
.bandSelected {
  opacity: 1;
}
.bandSeg {
  fill: none;
  stroke: var(--surface-sunken);
  transition: stroke var(--duration) var(--ease-out);
}
.bandSeg[data-level="0"] {
  stroke: var(--risk-0);
}
.bandSeg[data-level="1"] {
  stroke: var(--risk-1);
}
.bandSeg[data-level="2"] {
  stroke: var(--risk-2);
}
.bandSeg[data-level="3"] {
  stroke: var(--risk-3);
}
.bandPattern {
  fill: none;
}
.pat {
  fill: var(--risk-pattern);
}

.key {
  font: 600 9px var(--font-mono);
  fill: var(--ink-subtle);
}
.keySelected {
  fill: var(--ink);
}

.needle {
  stroke: var(--accent);
  stroke-width: 2.5;
}
.needleHead {
  fill: var(--accent);
}
.nowDot {
  fill: var(--accent);
}

.fade {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(90deg, var(--surface), transparent 16%, transparent 84%, var(--surface));
}

.readout {
  position: absolute;
  left: 50%;
  bottom: 0;
  transform: translateX(-50%);
  text-align: center;
  pointer-events: none;
}

.when {
  color: var(--ink);
  text-transform: lowercase;
}

.time {
  font: 600 40px / 1 var(--font-mono);
  letter-spacing: -0.04em;
  font-variant-numeric: tabular-nums;
}

.delta {
  margin-top: 4px;
  font: 500 var(--text-xs) / 1 var(--font-mono);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-muted);
}
